/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  GlobalStyles,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/joy";
import { Fragment, Suspense, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { CreateNewButton } from "../components";
import { BrandLogo } from "../components/BrandLogo";
import { CharacterAvatar } from "../components/CharacterAvatar";
import useCurrentConversationStore from "../stores/currentConversationStore";
import useGoogleLoginStore from "../stores/googleLoginStore";
import useLanguageStore from "../stores/languageStore";
import { checkPointsAvailability } from "../utils/pointsUtils";
import { getLocalizedPath } from "../utils/urlUtils";
import { AndroidSmartBanner } from "./AndroidSmartBanner";
import { ConversationControls } from "./ConversationControls";
import { GoogleAnalytics } from "./GoogleAnalytics";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SocialMediaIcons } from "./SocialMediaIcons";
import { LoginButton } from "./button-login";
import { Sidebar } from "./sidebar";
import {
  resolvePostLoginUserStatus,
  userDataToUserStatus,
} from "../utils/authStatus";

interface UserStatus {
  isAuthenticated: boolean;
  user?: {
    email: string;
    id: number;
    username: string;
    avatar: string;
    onboarding?: boolean;
  };
}

interface GoogleLoginStoreState {
  authenticated: boolean;
  setAuthenticated: (value: boolean) => void;
  setUserStatus: (value: UserStatus) => void;
  setOnboardingModalVisible: (value: boolean) => void;
}

/**
 * The main application layout.
 */
export function MainLayout({
  loginRequired,
}: {
  loginRequired: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const authenticated = useGoogleLoginStore(
    (state) => (state as GoogleLoginStoreState).authenticated,
  );
  const setAuthenticated = useGoogleLoginStore(
    (state) => (state as GoogleLoginStoreState).setAuthenticated,
  );
  const setUserStatus = useGoogleLoginStore(
    (state) => (state as GoogleLoginStoreState).setUserStatus,
  );
  const { currentLanguage } = useLanguageStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { characterId } = useParams();
  const msgPathMatch = location.pathname.match(/^\/messages\/([^/]+)$/);
  const messageId = msgPathMatch ? msgPathMatch[1] : null;
  const isMessagesPage =
    /^\/character\/[^/]+$/.test(location.pathname) ||
    /^\/messages\/[^/]+$/.test(location.pathname);
  const isCharacterInfoPage = /^\/character-info\/[^/]+$/.test(
    location.pathname,
  );
  const isHomePage =
    /^\/$/.test(location.pathname) || /^\/[a-z]{2}\/?$/.test(location.pathname);
  const isDiscoverPage =
    /^\/discover$/.test(location.pathname) ||
    /^\/[a-z]{2}\/discover$/.test(location.pathname);
  const isCreatorCenterPage =
    /^\/creator-center$/.test(location.pathname) ||
    /^\/[a-z]{2}\/creator-center$/.test(location.pathname);
  const isUserPersonaPage =
    /^\/user-persona$/.test(location.pathname) ||
    /^\/[a-z]{2}\/user-persona$/.test(location.pathname);
  const isMyImagesPage =
    /^\/my-images$/.test(location.pathname) ||
    /^\/[a-z]{2}\/my-images$/.test(location.pathname);
  const isNeedCreateButton = isCreatorCenterPage;
  const isHelpPage =
    /^\/(feedback|help)$/.test(location.pathname) ||
    /^\/[a-z]{2}\/(feedback|help)$/.test(location.pathname);
  const isAccountPage = /^\/account$/.test(location.pathname);
  const isMembershipPage = /^\/membership$/.test(location.pathname);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const conversation = useCurrentConversationStore(
    (state) => state.conversation,
  );

  // Get conversation info from URL params when conversation state is missing
  const getConversationFromUrl = () => {
    if (conversation) return conversation;

    // For character/messages routes, build a minimal conversation so mobile header renders immediately
    if (isMessagesPage && (messageId || characterId)) {
      return {
        characterName: "",
        avatarUrl: "",
        characterId: messageId || characterId || "",
      };
    }

    return null;
  };

  const effectiveConversation = getConversationFromUrl();

  // Ensure we have the correct characterId when on a messages page
  useEffect(() => {
    if (messageId && conversation && !characterId) {
      // Remove console log
    }
  }, [messageId, conversation, characterId]);

  const openConversationsDrawer = () => {
    // Dispatch a custom event that the messages component will listen for
    window.dispatchEvent(new CustomEvent("openConversationsDrawer"));
  };

  const openPersonaModal = () => {
    // Dispatch a custom event that the messages component will listen for
    window.dispatchEvent(new CustomEvent("openPersonaModal"));
  };

  // Optimize sidebar toggle with requestAnimationFrame
  const toggleSidebar = useCallback(() => {
    requestAnimationFrame(() => {
      const sidebar = document.querySelector(".Sidebar") as HTMLElement;
      if (sidebar) {
        sidebar.style.setProperty("--SideNavigation-slideIn", "1");
        const overlay = document.querySelector(
          ".sidebar-overlay",
        ) as HTMLElement;
        if (overlay) {
          overlay.style.opacity = "1";
          overlay.style.pointerEvents = "auto";
        }
      }
    });
  }, []);

  return (
    <Fragment>
      <GlobalStyles
        styles={{
          ":root": {
            "--Sidebar-width": "256px",
          },
          "#root": {
            width: "100vw",
            height: "100dvh",
            overflow: "hidden",
            position: "fixed",
            overscrollBehaviorY: "none",
          },
          "#main-scroll-container": {
            overscrollBehaviorY: "none",
          },
          // Add will-change for animated elements
          ".Sidebar": {
            willChange: "transform",
          },
          ".sidebar-overlay": {
            willChange: "opacity",
          },
        }}
      />
      <GoogleAnalytics />
      {(!loginRequired || authenticated) && (
        <>
          <AndroidSmartBanner
            showOnScroll={isHomePage}
            scrollOffset={120}
            scrollContainerSelector="#main-scroll-container"
          />
          <Box
            sx={{
              display: "flex",
              width: "100vw",
              height: "100dvh",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                py: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.surface",
                width: "100%",
                height: "64px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={toggleSidebar}
                  aria-label="Open menu"
                >
                  <MenuIcon />
                </IconButton>

                {/* Show BrandLogo only on wider screens or when no conversation is active */}
                {(isHomePage ||
                  isDiscoverPage ||
                  isCharacterInfoPage ||
                  (isMessagesPage && !effectiveConversation) ||
                  !authenticated) && (
                  <BrandLogo size={isHomePage ? "medium" : "small"} />
                )}

                {isMessagesPage && effectiveConversation && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0, // Allow shrinking
                        flex: 1, // Take available space but allow shrinking
                      }}
                    >
                      <Box
                        component={Link}
                        to={getLocalizedPath(
                          `/character-info/${effectiveConversation.characterId}`,
                          currentLanguage,
                        )}
                        sx={{
                          display: "flex",
                          cursor: "pointer",
                          borderRadius: "50%",
                          textDecoration: "none",
                          flexShrink: 0,
                          "&:hover": { opacity: 0.9 },
                        }}
                      >
                        <CharacterAvatar
                          src={effectiveConversation.avatarUrl}
                          name={effectiveConversation.characterName}
                          size="sm"
                          sx={{ width: 36, height: 36 }}
                        />
                      </Box>
                      <Typography
                        level="title-lg"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0, // Allow shrinking
                        }}
                      >
                        {effectiveConversation.characterName}
                      </Typography>
                    </Box>
                    {effectiveConversation.characterId && (
                      <ConversationControls
                        characterId={effectiveConversation.characterId}
                        onOpenConversationsDrawer={openConversationsDrawer}
                        onOpenPersonaModal={openPersonaModal}
                      />
                    )}
                  </>
                )}
              </Box>

              {authenticated ? (
                <Box sx={{ display: "flex", gap: 1, mr: 1 }}>
                  {isUserPersonaPage ? (
                    <CreateNewButton
                      startDecorator={<AddCircleOutlineRoundedIcon />}
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent("openPersonaCreateForm"),
                        );
                      }}
                    >
                      {t("myPersona.createButton")}
                    </CreateNewButton>
                  ) : isMyImagesPage ? (
                    <CreateNewButton
                      startDecorator={<AddCircleOutlineRoundedIcon />}
                      onClick={() => {
                        navigate(
                          getLocalizedPath("/create-image", currentLanguage),
                        );
                      }}
                    >
                      {t("myImages.createButton")}
                    </CreateNewButton>
                  ) : (
                    <CreateNewButton
                      startDecorator={<AddCircleOutlineRoundedIcon />}
                      sx={{
                        display: isNeedCreateButton ? "flex" : "none",
                      }}
                      onClick={async () => {
                        const result =
                          await checkPointsAvailability("create_character");
                        if (result?.success) {
                          navigate("/create-character");
                        } else {
                          setErrorMessage(
                            result?.errorMessage || "Error occurred",
                          );
                          setOpenSnackbar(true);
                        }
                      }}
                    >
                      {t("creatorCenter.createCharacter")}
                    </CreateNewButton>
                  )}
                </Box>
              ) : (
                !isHelpPage &&
                !(isHomePage || isDiscoverPage) && (
                  <LoginButton
                    signInMethod="google.com"
                    showRecommendedBadge={false}
                    onLoginSuccess={async (userData) => {
                      try {
                        const optimistic = userDataToUserStatus(userData);
                        const userStatus =
                          await resolvePostLoginUserStatus(optimistic);
                        setAuthenticated(true);
                        setUserStatus(userStatus);

                        // Check if user needs onboarding
                        if (
                          userStatus.user &&
                          userStatus.user.onboarding === false
                        ) {
                          // Trigger onboarding modal via global store
                          const { setOnboardingModalVisible } =
                            useGoogleLoginStore.getState() as GoogleLoginStoreState;
                          setOnboardingModalVisible(true);
                        }
                      } catch {
                        /* empty */
                      }
                    }}
                    sx={{
                      minWidth: { xs: "140px", sm: "220px" },
                      mr: 1,
                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      px: { xs: 2, sm: 3 },
                    }}
                  />
                )
              )}
              {isMembershipPage && (
                <Button
                  variant="outlined"
                  color="neutral"
                  size="sm"
                  sx={{
                    mr: 1,
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    px: { xs: 1.5, sm: 2 },
                    minWidth: { xs: "120px", sm: "160px" },
                  }}
                  onClick={() => {
                    navigate("/help");
                  }}
                >
                  {t("footer.contactSupport")}
                </Button>
              )}
              {(isDiscoverPage || (isHomePage && !authenticated)) && (
                <Box
                  sx={{
                    ml: 1,
                    display: { xs: isHomePage ? "none" : "block", md: "block" },
                  }}
                >
                  <SocialMediaIcons size={28} gap={1.5} mr={0} />
                </Box>
              )}
              {(isAccountPage || isHomePage) && (
                <Box sx={{ ml: 1 }}>
                  <LanguageSwitcher />
                </Box>
              )}
            </Box>
            <Sidebar />
            <Box
              component="main"
              id="main-scroll-container"
              sx={{
                marginLeft: {
                  xs: 0, // No margin on mobile
                  md: "var(--Sidebar-width)", // Margin on desktop
                },
                flexGrow: 1,
                height: "100dvh",
                overflow: "auto",
                position: "relative",
                width: {
                  xs: "100vw", // Full width on mobile
                  md: "calc(100vw - var(--Sidebar-width))", // Adjusted width on desktop
                },
              }}
            >
              <Suspense>
                <Outlet />
              </Suspense>
            </Box>
          </Box>
          <Snackbar
            variant="solid"
            color="danger"
            size="lg"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
            autoHideDuration={5000}
            sx={{
              width: "auto",
              maxWidth: {
                xs: "calc(100% - 32px)",
                sm: "calc(100% - 48px)",
                md: 360,
              },
              mt: 1,
            }}
          >
            {errorMessage}
          </Snackbar>
        </>
      )}
    </Fragment>
  );
}

/**
 * The minimal app layout to be used on pages such Login/Signup,
 * Privacy Policy, Terms of Use, etc.
 */
export function BaseLayout(): JSX.Element {
  return (
    <Fragment>
      <GlobalStyles
        styles={{
          "#root": {
            display: "grid",
            gridTemplateColumns: "1fr",
            minHeight: "100dvh",
          },
        }}
      />
      <GoogleAnalytics />
      <Box sx={{ gridArea: "1 / 1 / -1 / -1", pt: "60px" }}>
        <Suspense>
          <Outlet />
        </Suspense>
      </Box>
    </Fragment>
  );
}
